import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import ComboFiltro from '../../../components/ComboFiltro';
import { apiFetch } from '../../../lib/api';
import { showToast } from '../../../components/Toast';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatQuantity } from '../../../utils/formatters';
import { API_DOCS } from './VistaFabbisogno.api';
import './VistaFabbisogno.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type Origine = 'ORDINE_VENDITA' | 'DISTINTA';

interface Alternativo {
  articoloId: number;
  codice: string;
  descrizione: string;
  giacenza: number;
}

interface RigaFabbisogno {
  chiave: string;
  origine: Origine;
  societaId: number;
  commessa: {
    id: number;
    numero: string;
    descrizione: string;
    clienteRagioneSociale: string;
  } | null;
  ordine: { id: number; numero: string; dataConferma: string | null };
  distinta: {
    id: number;
    numero: number;
    etichetta: string;
    addebito: string;
    confermataAt: string | null;
  } | null;
  rigaOrdineId: number | null;
  componenteId: number | null;
  rigaDistintaId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  tipologia: string | null;
  unitaMisura: string | null;
  quantita: number;
  quantitaEvasa: number;
  residua: number;
  inRda: number;
  residuoDaOrdinare: number;
  giacenza: number | null;
  alternativi: Alternativo[];
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

interface GruppoArticolo {
  chiave: string;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  unitaMisura: string | null;
  tipologia: string | null;
  giacenza: number | null;
  totaleResidua: number;
  totaleInRda: number;
  totaleDaOrdinare: number;
  alternativi: Alternativo[];
  sorgenti: RigaFabbisogno[];
}

interface VistaFabbisognoProps {
  categoria: 'merce' | 'subappalti';
  titolo: string;
  /** Codice modulo permesso della voce di menu (la route è già protetta in App.tsx) */
  permessoModulo: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function messaggioErrore(e: unknown): string {
  return e instanceof Error ? e.message : 'Errore nel caricamento dei fabbisogni';
}

function testoErroreApi(
  error: string | { code?: string; message?: string } | undefined,
  fallback = 'Errore nel caricamento dei fabbisogni'
): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || fallback;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function badgeCopertura(g: GruppoArticolo): React.ReactNode {
  if (g.articoloId == null || g.giacenza == null) {
    return <span className="bop-text-muted">—</span>;
  }
  if (g.giacenza >= g.totaleResidua) {
    return <span className="bop-badge bop-badge-success">Disponibile</span>;
  }
  if (g.giacenza > 0) {
    return <span className="bop-badge bop-badge-warning">Parziale</span>;
  }
  return <span className="bop-badge bop-badge-danger">Da ordinare</span>;
}

// Quantità con "—" quando il valore è zero (colonne In RDA / Da ordinare)
function cellaQuantitaOpzionale(v: number): React.ReactNode {
  return v > 0 ? formatQuantity(v) : <span className="bop-text-muted">—</span>;
}

// Quantità di default per la selezione RDA (stringa con virgola)
function quantitaDefault(r: RigaFabbisogno): string {
  return String(r.residuoDaOrdinare ?? 0).replace('.', ',');
}

// ─────────────────────────────────────────────────────────────────────────────
// VistaFabbisogno — vista in tempo reale dei fabbisogni (merce o subappalti),
// calcolata dal backend su ordini confermati e distinte confermate.
// Dalla vista è possibile selezionare le righe sorgente con residuo da
// ordinare e generare una RDA (permesso acquisti.rda edit).
// ─────────────────────────────────────────────────────────────────────────────
export default function VistaFabbisogno({ categoria, titolo }: VistaFabbisognoProps) {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();

  // ?commessaId=X&societaId=Y: link "Richiedi acquisto" dal cruscotto commessa
  const [searchParams] = useSearchParams();

  // Stato locale società (solo SUPERADMIN con "Tutte" in topbar può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState(() => searchParams.get('societaId') || '');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri — ?commessaId=X preseleziona la commessa
  const [commessaId, setCommessaId] = useState(() => searchParams.get('commessaId') || '');
  const [origine, setOrigine] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [righe, setRighe] = useState<RigaFabbisogno[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Gruppi espansi (chiave gruppo → aperto)
  const [espansi, setEspansi] = useState<Set<string>>(new Set());

  // Selezione righe sorgente per generazione RDA: chiave riga → quantità (stringa con virgola)
  const [selezione, setSelezione] = useState<Map<string, string>>(new Map());
  const [modalRdaAperta, setModalRdaAperta] = useState(false);
  const [noteRda, setNoteRda] = useState('');
  const [generandoRda, setGenerandoRda] = useState(false);

  const puoGenerareRda = can('acquisti.rda', 'edit');
  // La generazione richiede una società selezionata (no cross-società)
  const selezioneAttiva = puoGenerareRda && !!societaIdEff;

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Carica fabbisogni ───────────────────────────────────────────────────────
  const caricaFabbisogni = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (commessaId) params.set('commessaId', commessaId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/fabbisogni/merce?societaId&commessaId -> {"success":true,"data":[{"chiave":"string","origine":"ORDINE_VENDITA|DISTINTA","societaId":"number","commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"ordine":{"id":"number","numero":"string","dataConferma":"string"},"distinta":{"id":"number","numero":"number","etichetta":"string","addebito":"string","confermataAt":"string"},"rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","articoloId":"number|null","codice":"string|null","descrizione":"string","tipologia":"string|null","unitaMisura":"string|null","quantita":"number","quantitaEvasa":"number","residua":"number","inRda":"number","residuoDaOrdinare":"number","giacenza":"number|null","alternativi":[{"articoloId":"number","codice":"string","descrizione":"string","giacenza":"number"}]}]}
      // @api GET /api/fabbisogni/subappalti?societaId&commessaId -> {"success":true,"data":[{"chiave":"string","origine":"ORDINE_VENDITA|DISTINTA","societaId":"number","commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"ordine":{"id":"number","numero":"string","dataConferma":"string"},"distinta":{"id":"number","numero":"number","etichetta":"string","addebito":"string","confermataAt":"string"},"rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","articoloId":"number|null","codice":"string|null","descrizione":"string","tipologia":"string|null","unitaMisura":"string|null","quantita":"number","quantitaEvasa":"number","residua":"number","inRda":"number","residuoDaOrdinare":"number","giacenza":"number|null","alternativi":[{"articoloId":"number","codice":"string","descrizione":"string","giacenza":"number"}]}]}
      const r = await apiFetch<{
        success: boolean;
        data?: RigaFabbisogno[];
        error?: string | { code?: string; message?: string };
      }>(`/api/fabbisogni/${categoria}${qs}`);

      if (!r.success) {
        showToast(testoErroreApi(r.error), 'error');
        setRighe([]);
      } else {
        setRighe(r.data || []);
      }
    } catch (err: unknown) {
      showToast(messaggioErrore(err), 'error');
      setRighe([]);
    } finally {
      // Ogni ricaricamento invalida la selezione (le chiavi potrebbero cambiare)
      setSelezione(new Map());
      setLoading(false);
    }
  }, [pronta, societaIdEff, commessaId, categoria]);

  useEffect(() => { caricaFabbisogni(); }, [caricaFabbisogni]);

  // ── Carica commesse per il combo (dipende dalla società effettiva) ─────────
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Reset commessa quando cambia la società — non al primo valore, che
  // conserva la commessa arrivata dall'URL
  const societaPrecRef = useRef<string | null>(null);
  useEffect(() => {
    if (societaPrecRef.current !== null && societaPrecRef.current !== societaIdEff) setCommessaId('');
    if (societaIdEff) societaPrecRef.current = societaIdEff;
  }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // ── Filtro client-side (origine + ricerca testo) ───────────────────────────
  const righeFiltrate = useMemo(() => {
    const t = q.toLowerCase();
    return righe.filter(r => {
      if (origine && r.origine !== origine) return false;
      if (t) {
        const campi = [
          r.codice || '',
          r.descrizione || '',
          r.commessa ? `${r.commessa.numero} ${r.commessa.descrizione || ''} ${r.commessa.clienteRagioneSociale || ''}` : '',
          r.ordine ? r.ordine.numero : '',
          r.distinta ? r.distinta.etichetta : '',
        ].join(' ').toLowerCase();
        if (!campi.includes(t)) return false;
      }
      return true;
    });
  }, [righe, origine, q]);

  // ── KPI ─────────────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const totaleDaOrdinare = righeFiltrate.reduce((s, r) => s + (r.residuoDaOrdinare || 0), 0);
    const totaleInRda = righeFiltrate.reduce((s, r) => s + (r.inRda || 0), 0);
    const daApprovvigionare = righeFiltrate.filter(
      r => r.articoloId != null && r.giacenza != null && r.giacenza < r.residua
    ).length;
    const senzaArticolo = righeFiltrate.filter(r => r.articoloId == null).length;
    return { righe: righeFiltrate.length, totaleDaOrdinare, totaleInRda, daApprovvigionare, senzaArticolo };
  }, [righeFiltrate]);

  // ── Raggruppamento per articolo ─────────────────────────────────────────────
  const gruppi = useMemo(() => {
    const map = new Map<string, GruppoArticolo>();
    for (const r of righeFiltrate) {
      const k = r.articoloId != null ? `A-${r.articoloId}` : `S-${r.descrizione}`;
      let g = map.get(k);
      if (!g) {
        g = {
          chiave: k,
          articoloId: r.articoloId,
          codice: r.codice,
          descrizione: r.descrizione,
          unitaMisura: r.unitaMisura,
          tipologia: r.tipologia,
          giacenza: r.giacenza,
          totaleResidua: 0,
          totaleInRda: 0,
          totaleDaOrdinare: 0,
          alternativi: r.alternativi || [],
          sorgenti: [],
        };
        map.set(k, g);
      }
      if (g.giacenza == null && r.giacenza != null) g.giacenza = r.giacenza;
      if (g.alternativi.length === 0 && (r.alternativi || []).length > 0) g.alternativi = r.alternativi;
      if (!g.unitaMisura && r.unitaMisura) g.unitaMisura = r.unitaMisura;
      g.totaleResidua += r.residua || 0;
      g.totaleInRda += r.inRda || 0;
      g.totaleDaOrdinare += r.residuoDaOrdinare || 0;
      g.sorgenti.push(r);
    }
    const lista = Array.from(map.values());
    lista.sort((a, b) => {
      // I gruppi "Senza articolo" in fondo
      const aSenza = a.articoloId == null ? 1 : 0;
      const bSenza = b.articoloId == null ? 1 : 0;
      if (aSenza !== bSenza) return aSenza - bSenza;
      return (a.codice || a.descrizione).localeCompare(b.codice || b.descrizione, 'it');
    });
    return lista;
  }, [righeFiltrate]);

  const toggleGruppo = (k: string) => {
    setEspansi(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const pulisciFiltri = () => {
    setCommessaId('');
    setOrigine('');
    setQInput('');
    setQ('');
  };

  // ── Selezione righe sorgente per RDA ────────────────────────────────────────

  const toggleSorgente = (r: RigaFabbisogno) => {
    if ((r.residuoDaOrdinare || 0) <= 0) return;
    setSelezione(prev => {
      const next = new Map(prev);
      if (next.has(r.chiave)) next.delete(r.chiave);
      else next.set(r.chiave, quantitaDefault(r));
      return next;
    });
  };

  const toggleGruppoSelezione = (g: GruppoArticolo) => {
    const selezionabili = g.sorgenti.filter(s => (s.residuoDaOrdinare || 0) > 0);
    if (selezionabili.length === 0) return;
    setSelezione(prev => {
      const next = new Map(prev);
      const tutteSelezionate = selezionabili.every(s => next.has(s.chiave));
      if (tutteSelezionate) {
        selezionabili.forEach(s => next.delete(s.chiave));
      } else {
        selezionabili.forEach(s => { if (!next.has(s.chiave)) next.set(s.chiave, quantitaDefault(s)); });
      }
      return next;
    });
  };

  const setQuantitaSelezionata = (chiave: string, val: string) => {
    setSelezione(prev => {
      if (!prev.has(chiave)) return prev;
      const next = new Map(prev);
      next.set(chiave, val);
      return next;
    });
  };

  // Righe selezionate in ordine stabile (stesso ordine del caricamento)
  const righeSelezionate = useMemo(
    () => righe
      .filter(r => selezione.has(r.chiave))
      .map(r => ({ riga: r, quantitaStr: selezione.get(r.chiave) as string })),
    [righe, selezione]
  );

  const totaleQtaSelezionata = useMemo(
    () => righeSelezionate.reduce((s, { quantitaStr }) => {
      const n = Number(String(quantitaStr).replace(',', '.'));
      return Number.isFinite(n) && n > 0 ? s + n : s;
    }, 0),
    [righeSelezionate]
  );

  // Valida le quantità selezionate: ritorna null (e mostra un toast) se una non è valida
  const validaSelezione = (): boolean => {
    for (const { riga, quantitaStr } of righeSelezionate) {
      const n = Number(String(quantitaStr).replace(',', '.'));
      if (!Number.isFinite(n) || n <= 0) {
        showToast(`Quantità non valida per "${riga.descrizione}"`, 'error');
        return false;
      }
    }
    return true;
  };

  const apriModalRda = () => {
    if (righeSelezionate.length === 0) {
      showToast('Seleziona almeno una riga da ordinare', 'error');
      return;
    }
    if (!validaSelezione()) return;
    setModalRdaAperta(true);
  };

  // ── Generazione RDA ─────────────────────────────────────────────────────────
  const generaRda = async () => {
    if (!societaIdEff) {
      showToast('Seleziona una società per generare la RDA', 'error');
      return;
    }
    if (!validaSelezione()) return;
    const righePayload = righeSelezionate.map(({ riga, quantitaStr }) => ({
      articoloId: riga.articoloId,
      descrizione: riga.descrizione,
      unitaMisura: riga.unitaMisura,
      quantita: Number(String(quantitaStr).replace(',', '.')),
      rigaOrdineId: riga.rigaOrdineId,
      componenteId: riga.componenteId,
      rigaDistintaId: riga.rigaDistintaId,
      ordineId: riga.ordine?.id ?? null,
      commessaId: riga.commessa?.id ?? null,
    }));
    if (righePayload.length === 0) {
      showToast('Seleziona almeno una riga da ordinare', 'error');
      return;
    }

    setGenerandoRda(true);
    try {
      // @api POST /api/rda/genera-da-fabbisogno {"societaId":"number","note":"string|null","righe":[{"articoloId":"number|null","descrizione":"string","unitaMisura":"string|null","quantita":"number","rigaOrdineId":"number|null","componenteId":"number|null","rigaDistintaId":"number|null","ordineId":"number|null","commessaId":"number|null"}]} -> {"success":true,"data":{"id":"number","etichetta":"string"}}
      const r = await apiFetch<{
        success: boolean;
        data?: { id: number; etichetta: string };
        error?: string | { code?: string; message?: string };
      }>('/api/rda/genera-da-fabbisogno', {
        method: 'POST',
        body: JSON.stringify({
          societaId: parseInt(societaIdEff, 10),
          note: noteRda.trim() || null,
          righe: righePayload,
        }),
      });
      if (!r.success || !r.data) {
        showToast(testoErroreApi(r.error, 'Errore nella generazione della RDA'), 'error');
        return;
      }
      showToast(`${r.data.etichetta} generata`, 'success');
      navigate(`/commerciale/passivo/rda/${r.data.id}`);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella generazione della RDA'), 'error');
    } finally {
      setGenerandoRda(false);
    }
  };

  // Numero colonne della tabella principale (per colSpan di stati e drill-down)
  const numColonne = selezioneAttiva ? 10 : 9;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="fabb-page">
        {/* Header */}
        <div className="bop-page-header">
          <div className="fabb-header-text">
            <h1 className="bop-page-title">{titolo}</h1>
            <p className="fabb-subtitle bop-text-muted">
              Vista in tempo reale — calcolata da ordini confermati e distinte confermate
            </p>
          </div>
        </div>

        {/* KPI */}
        <div className="fabb-kpi-grid">
          <div className="bop-card fabb-kpi">
            <div className="fabb-kpi__label">Righe fabbisogno</div>
            <div className="fabb-kpi__value">{kpi.righe}</div>
          </div>
          <div className="bop-card fabb-kpi">
            <div className="fabb-kpi__label">Da ordinare</div>
            <div className={`fabb-kpi__value ${kpi.totaleDaOrdinare > 0 ? 'fabb-kpi__value--danger' : ''}`}>
              {formatQuantity(kpi.totaleDaOrdinare)}
            </div>
          </div>
          <div className="bop-card fabb-kpi">
            <div className="fabb-kpi__label">In RDA</div>
            <div className="fabb-kpi__value">{formatQuantity(kpi.totaleInRda)}</div>
          </div>
          <div className="bop-card fabb-kpi">
            <div className="fabb-kpi__label">Articoli da approvvigionare</div>
            <div className={`fabb-kpi__value ${kpi.daApprovvigionare > 0 ? 'fabb-kpi__value--danger' : ''}`}>
              {kpi.daApprovvigionare}
            </div>
          </div>
          <div className="bop-card fabb-kpi">
            <div className="fabb-kpi__label">Righe senza articolo codificato</div>
            <div className={`fabb-kpi__value ${kpi.senzaArticolo > 0 ? 'fabb-kpi__value--warning' : ''}`}>
              {kpi.senzaArticolo}
            </div>
          </div>
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar fabb-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group fabb-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Origine</label>
            <select
              className="bop-select"
              value={origine}
              onChange={e => setOrigine(e.target.value)}
            >
              <option value="">Tutte</option>
              <option value="ORDINE_VENDITA">Ordini vendita</option>
              <option value="DISTINTA">Distinte</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input fabb-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Codice, descrizione, commessa, ordine..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella raggruppata per articolo */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare i fabbisogni</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table">
              <thead>
                <tr>
                  {selezioneAttiva && <th className="bop-table__th--select"></th>}
                  <th className="fabb-th-expander"></th>
                  <th>Codice</th>
                  <th>Descrizione</th>
                  <th>U.M.</th>
                  <th className="bop-text-right">Fabbisogno totale</th>
                  <th className="bop-text-right">In RDA</th>
                  <th className="bop-text-right">Da ordinare</th>
                  <th className="bop-text-right">Giacenza</th>
                  <th>Copertura</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={numColonne}>
                      <div className="bop-empty-state">Caricamento fabbisogni…</div>
                    </td>
                  </tr>
                ) : gruppi.length === 0 ? (
                  <tr>
                    <td colSpan={numColonne}>
                      <div className="bop-empty-state">Nessun fabbisogno trovato con i filtri attuali.</div>
                    </td>
                  </tr>
                ) : (
                  gruppi.map(g => {
                    const espanso = espansi.has(g.chiave);
                    const alternativiOrdinati = [...g.alternativi]
                      .sort((a, b) => (b.giacenza || 0) - (a.giacenza || 0));
                    const selezionabili = g.sorgenti.filter(s => (s.residuoDaOrdinare || 0) > 0);
                    const numSelezionate = selezionabili.filter(s => selezione.has(s.chiave)).length;
                    const tutteSelezionate = selezionabili.length > 0 && numSelezionate === selezionabili.length;
                    const daOrdinareDanger =
                      g.totaleDaOrdinare > 0 && (g.giacenza ?? 0) < g.totaleDaOrdinare;
                    return (
                      <React.Fragment key={g.chiave}>
                        <tr className="fabb-row-gruppo" onClick={() => toggleGruppo(g.chiave)}>
                          {selezioneAttiva && (
                            <td className="bop-table__td--select">
                              <input
                                type="checkbox"
                                className="fabb-check"
                                checked={tutteSelezionate}
                                disabled={selezionabili.length === 0}
                                ref={el => {
                                  if (el) el.indeterminate = numSelezionate > 0 && !tutteSelezionate;
                                }}
                                onClick={e => e.stopPropagation()}
                                onChange={() => toggleGruppoSelezione(g)}
                                aria-label="Seleziona tutte le sorgenti da ordinare dell'articolo"
                              />
                            </td>
                          )}
                          <td className="fabb-td-expander">
                            <button
                              type="button"
                              className="fabb-expander"
                              aria-label={espanso ? 'Comprimi dettaglio' : 'Espandi dettaglio'}
                              onClick={e => { e.stopPropagation(); toggleGruppo(g.chiave); }}
                            >
                              {espanso ? '−' : '+'}
                            </button>
                          </td>
                          <td>
                            {g.codice
                              ? <span className="fabb-codice">{g.codice}</span>
                              : <span className="bop-badge bop-badge-neutral">Senza articolo</span>}
                          </td>
                          <td>{g.descrizione}</td>
                          <td>{g.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                          <td className="bop-text-right">{formatQuantity(g.totaleResidua)}</td>
                          <td className="bop-text-right">{cellaQuantitaOpzionale(g.totaleInRda)}</td>
                          <td className={`bop-text-right ${daOrdinareDanger ? 'bop-text-danger fabb-da-ordinare--danger' : ''}`}>
                            {cellaQuantitaOpzionale(g.totaleDaOrdinare)}
                          </td>
                          <td className="bop-text-right">
                            {g.giacenza != null
                              ? formatQuantity(g.giacenza)
                              : <span className="bop-text-muted">—</span>}
                          </td>
                          <td>{badgeCopertura(g)}</td>
                        </tr>
                        {espanso && (
                          <tr className="fabb-row-dettaglio">
                            <td colSpan={numColonne}>
                              <div className="fabb-dettaglio">
                                {/* (a) Sorgenti del fabbisogno */}
                                <div className="fabb-sorgenti">
                                  <div className="fabb-dettaglio__title">Sorgenti del fabbisogno</div>
                                  <table className="bop-table">
                                    <thead>
                                      <tr>
                                        {selezioneAttiva && <th className="bop-table__th--select"></th>}
                                        <th>Origine</th>
                                        <th>Ordine</th>
                                        <th>Commessa</th>
                                        <th className="bop-text-right">Quantità</th>
                                        <th className="bop-text-right">Evasa</th>
                                        <th className="bop-text-right">Residua</th>
                                        <th className="bop-text-right">In RDA</th>
                                        <th className="bop-text-right">Da ordinare</th>
                                        {selezioneAttiva && <th className="bop-text-right">Q.tà da ordinare</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {g.sorgenti.map(s => {
                                        const selezionata = selezione.has(s.chiave);
                                        const selezionabile = (s.residuoDaOrdinare || 0) > 0;
                                        return (
                                          <tr key={s.chiave}>
                                            {selezioneAttiva && (
                                              <td className="bop-table__td--select">
                                                <input
                                                  type="checkbox"
                                                  className="fabb-check"
                                                  checked={selezionata}
                                                  disabled={!selezionabile}
                                                  onChange={() => toggleSorgente(s)}
                                                  aria-label="Seleziona sorgente per la RDA"
                                                />
                                              </td>
                                            )}
                                            <td>
                                              {s.origine === 'ORDINE_VENDITA' ? (
                                                <span className="bop-badge bop-badge-info">Ordine vendita</span>
                                              ) : (
                                                <span className="fabb-origine-distinta">
                                                  <span className="bop-badge bop-badge-neutral">Distinta</span>
                                                  {s.distinta && (
                                                    <span className="fabb-origine-etichetta">{s.distinta.etichetta}</span>
                                                  )}
                                                </span>
                                              )}
                                            </td>
                                            <td>
                                              {s.ordine
                                                ? s.ordine.numero
                                                : <span className="bop-text-muted">—</span>}
                                            </td>
                                            <td>
                                              {s.commessa
                                                ? (s.commessa.clienteRagioneSociale
                                                    ? `${s.commessa.numero} — ${s.commessa.clienteRagioneSociale}`
                                                    : s.commessa.numero)
                                                : <span className="bop-text-muted">—</span>}
                                            </td>
                                            <td className="bop-text-right">{formatQuantity(s.quantita)}</td>
                                            <td className="bop-text-right">{formatQuantity(s.quantitaEvasa)}</td>
                                            <td className="bop-text-right">{formatQuantity(s.residua)}</td>
                                            <td className="bop-text-right">{cellaQuantitaOpzionale(s.inRda)}</td>
                                            <td className="bop-text-right">{cellaQuantitaOpzionale(s.residuoDaOrdinare)}</td>
                                            {selezioneAttiva && (
                                              <td className="bop-text-right">
                                                <input
                                                  type="text"
                                                  className="bop-input fabb-qta-input"
                                                  value={selezionata ? selezione.get(s.chiave) ?? '' : ''}
                                                  disabled={!selezionata}
                                                  onChange={e => setQuantitaSelezionata(s.chiave, e.target.value)}
                                                  placeholder={selezionabile ? quantitaDefault(s) : ''}
                                                  aria-label="Quantità da ordinare"
                                                />
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                {/* (b) Alternativi a magazzino — solo articoli intercambiabili */}
                                {g.tipologia === 'INTERCAMBIABILE' && (
                                  <div className="fabb-alt">
                                    <div className="fabb-dettaglio__title">Alternativi a magazzino</div>
                                    {alternativiOrdinati.length === 0 ? (
                                      <div className="bop-text-muted fabb-alt__empty">
                                        Nessun articolo alternativo disponibile.
                                      </div>
                                    ) : (
                                      <ul className="fabb-alt__list">
                                        {alternativiOrdinati.map(a => (
                                          <li key={a.articoloId} className="fabb-alt__item">
                                            <span className="fabb-alt__codice">{a.codice}</span>
                                            <span className="fabb-alt__desc">{a.descrizione}</span>
                                            {a.giacenza > 0 ? (
                                              <span className="bop-badge bop-badge-success">
                                                {formatQuantity(a.giacenza)}
                                              </span>
                                            ) : (
                                              <span className="bop-badge bop-badge-neutral">
                                                {formatQuantity(a.giacenza)}
                                              </span>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
            {!loading && gruppi.length > 0 && (
              <div className="fabb-record-count bop-text-muted">
                {gruppi.length} articoli — {righeFiltrate.length} righe di fabbisogno
              </div>
            )}
          </div>
        )}

        {/* Barra azioni sticky — visibile con selezioni attive */}
        {selezioneAttiva && righeSelezionate.length > 0 && (
          <div className="fabb-selection-bar">
            <div className="fabb-selection-bar__info">
              <strong>{righeSelezionate.length}</strong>
              {righeSelezionate.length === 1 ? ' riga selezionata' : ' righe selezionate'}
              {' — totale q.tà '}
              <strong>{formatQuantity(totaleQtaSelezionata)}</strong>
            </div>
            <div className="fabb-selection-bar__actions">
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={() => setSelezione(new Map())}
              >
                Annulla selezione
              </button>
              <button
                type="button"
                className="bop-btn bop-btn-primary"
                onClick={apriModalRda}
              >
                Genera RDA
              </button>
            </div>
          </div>
        )}

        {/* Modale conferma generazione RDA */}
        {modalRdaAperta && (
          <div className="bop-modal-overlay" onClick={() => { if (!generandoRda) setModalRdaAperta(false); }}>
            <div className="bop-modal bop-modal--lg" onClick={e => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Genera RDA da fabbisogno</h2>
                <button
                  type="button"
                  className="bop-modal__close"
                  onClick={() => { if (!generandoRda) setModalRdaAperta(false); }}
                  aria-label="Chiudi"
                >
                  ×
                </button>
              </div>
              <div className="bop-modal__body">
                <p className="fabb-rda-intro bop-text-muted">
                  Verrà creata una richiesta di acquisto con le righe selezionate.
                </p>
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>U.M.</th>
                      <th className="bop-text-right">Quantità</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righeSelezionate.map(({ riga, quantitaStr }) => {
                      const n = Number(String(quantitaStr).replace(',', '.'));
                      return (
                        <tr key={riga.chiave}>
                          <td>
                            {riga.codice
                              ? <span className="fabb-codice">{riga.codice}</span>
                              : <span className="bop-badge bop-badge-neutral">Senza articolo</span>}
                          </td>
                          <td>{riga.descrizione}</td>
                          <td>{riga.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                          <td className="bop-text-right">
                            {Number.isFinite(n) && n > 0 ? formatQuantity(n) : quantitaStr}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bop-form-group fabb-rda-note-group">
                  <label className="bop-label">Note</label>
                  <textarea
                    className="bop-input fabb-rda-note"
                    rows={3}
                    value={noteRda}
                    onChange={e => setNoteRda(e.target.value)}
                    placeholder="Note per la richiesta di acquisto (facoltative)"
                  />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button
                  type="button"
                  className="bop-btn bop-btn-secondary"
                  onClick={() => setModalRdaAperta(false)}
                  disabled={generandoRda}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="bop-btn bop-btn-primary"
                  onClick={generaRda}
                  disabled={generandoRda || righeSelezionate.length === 0}
                >
                  {generandoRda ? 'Generazione…' : 'Genera RDA'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
